import { BaseFieldClass } from './baseField.class'
import { PossibleValue } from './possibleValue.class'

import { IListIntField, IListField, IPossibleValue } from '@/types/serverField.type'
import { getValueFromObject } from '@/utils/getValueFromObject'

export class ListFieldClass extends BaseFieldClass {
  declare fieldValue: PossibleValue | PossibleValue[] | string[] | null
  declare initialValue: PossibleValue | PossibleValue[] | string[] | null

  constructor (field: IListIntField | IListField) {
    super(field)

    if (this.widgetType === 'checkbox' || this.filterOperator !== 'exact') {
      this.possibleValues.forEach(item => {
        item.value = String(item.value)
      })
    }

    this.initialValue = this.prepareValue(field.Value)
    this.fieldValue = this.prepareValue(field.Value)
  }

  private getValues (value: unknown): unknown[] {
    if (Array.isArray(value)) return value
    if (value === null || value === undefined) return []

    if (typeof value === 'object' && getValueFromObject(value) === value) {
      return Object.values(value)
    }

    return [value]
  }

  private findPossibleValue (value: unknown): PossibleValue | undefined {
    const rawValue = getValueFromObject(value)
    return this.possibleValues.find(item => String(item.value) === String(rawValue))
  }

  prepareValue (value: unknown): PossibleValue | PossibleValue[] | string[] | null {
    const preparedValue = value ?? this.defaultValue

    // checkbox — множественный выбор
    if (this.widgetType === 'checkbox') {
      return this.getValues(preparedValue)
        .map(item => getValueFromObject(item))
        .filter(item => item !== null && item !== undefined)
        .map(item => String(item))
    }

    if (this.multipleValues) {
      return this.getValues(preparedValue)
        .map(item => this.findPossibleValue(item))
        .filter((item): item is PossibleValue => item !== undefined)
    }

    // select / radio — одиночный выбор
    const found = this.findPossibleValue(preparedValue)
    if (found) return found
    if (this.defaultValue !== null && this.defaultValue !== undefined) {
      const rawDefaultValue = getValueFromObject(this.defaultValue)
      return this.possibleValues.find(
        item => String(item.value) === String(rawDefaultValue)
      ) || new PossibleValue(
        typeof this.defaultValue === 'object'
          ? this.defaultValue as IPossibleValue
          : {
              Value: rawDefaultValue as IPossibleValue['Value'],
              Title: '',
              Required: false,
              Disabled: false,
            }
      )
    }
    return this.possibleValues?.[0] ?? null
  }

  getValue (): Record<string, unknown> {
    const result: Record<string, unknown> = {}

    if (this.widgetType === 'checkbox') {
      const values = this.value as string[]
      if (values?.length) {
        values.forEach(item => {
          if (item) result[`${this.name}__${item}`] = item
        })
      } else {
        result[this.name] = null
      }
    } else {
      const val = this.value as PossibleValue
      result[this.name] = typeof val === 'object' ? val?.value : val
    }

    return result
  }

  override valueToServer (): unknown {
    if (this.widgetType === 'checkbox' || this.multipleValues) {
      const result: Record<string, unknown> = {}
      const values = Array.isArray(this.value) ? this.value : []

      values.forEach(item => {
        const value = getValueFromObject(item)
        if (value !== null && value !== undefined && value !== '') {
          result[String(value)] = value
        }
      })

      return Object.keys(result).length ? result : null
    }

    const value = getValueFromObject(this.value)
    return value === 'null' ? null : value ?? null
  }

  appendToFormData (formData: FormData): void {
    if (this.widgetType === 'checkbox') {
      const values = this.value as unknown[]
      if (values?.length) {
        values.forEach(item => {
          if (item) formData.append(`${this.name}__${item}`, String(item))
        })
      } else if (typeof this.value === 'boolean') {
        formData.append(this.name, String(this.value))
      } else {
        formData.append(this.name, 'null')
      }
      return
    }

    // select / radio
    const val = this.value as { value: string }
    formData.append(this.name, val?.value ?? '')
  }

  getFilterValue () {
    if (this.widgetType === 'select' && this.multipleValues) {
      const result: Record<string, unknown> = {}
      const values = Array.isArray(this.value) ? this.value : [this.value]
      values.forEach((item: any) => {
        result[item.id] = item.value
      })
      return result
    }

    const val = (this.value as any)?.value
    if (val === 'null') return null
    if (val === 'true' || val === 'false') return val === 'true'
    return val
  }

  setFilterValue (value: unknown): void {
    if (this.multipleValues) {
      this.fieldValue = String(value).split(',').map(item =>
        this.possibleValues?.find(val => String(val.value) === item)
      ).filter(Boolean) as PossibleValue[]
      return
    }

    let found = this.possibleValues?.find(val => String(val.value) === String(value))

    // Иногда в query параметрах приходят 'true'/'false' строками, в то время когда в possibleValues нет таких значений
    // В этом случае попробуем привести строку к числу и найти значение
    if (!found && (value === 'true' || value === 'false')) {
      const numericValue = value === 'true' ? 1 : 0
      found = this.possibleValues?.find(val => String(val.value) === String(numericValue))
    }

    if (found) this.fieldValue = found
  }

  getValueAsText (): string {
    if (this.multipleValues) {
      const values = this.value as Array<PossibleValue | string>
      return values?.map(item => typeof item === 'string' ? item : item.title).join(', ') ?? ''
    }

    const val = this.value as PossibleValue
    return typeof val === 'object' ? val?.title : val
  }
}
